import { DayView, Scheduler, WeekView } from "@progress/kendo-react-scheduler";
import {  NameTitleTimeItem } from "../calendar/components";
import { DropdownOption, MultiSelectDropdownOptions, MultiSelectProviderDropdown, SearchableProviderDropdown } from "../../components";
import { useCalendarEvents } from "../calendar/hooks";
import { useAsync, useUrlParams } from "../../hooks";
import { LocationService } from "../../services";
import { useMemo, useState } from "react";
import { Day } from "@progress/kendo-date-math";
import { DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";

export function CalendarPage(){

  const locationCode = useUrlParams("locationCode");
  const locationServices = useMemo(() => new LocationService(), []);
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [selectedMultiProvider, setSelectedMultiProvider] = useState<any[]>([]);
  const [multiSelectOptions, setMultiSelectOptions] = useState<DropdownOption[]>([]);
  const [classicDropdownOptions, setClassicDropdownOptions] = useState<DropdownOption[]>([]);
  const [activeView, setActiveView] = useState<string>("day");
  const { date, mapResult, group, resources, setCalendarGrouping, handleInputChange, refreshCalendarEvents} = useCalendarEvents();

  const { value: locationName } = useAsync(async () => {
    let providers = await locationServices.getProvidersByLocationCode(locationCode);

    let locationId = providers && providers.length > 0 ? providers[0].locationId : null;
    let location = await locationServices.get(locationId ?? -1);
    let locationName = location?.name ?? "";

    const baseOptions = providers?.map((provider) => ({
      text: provider.name,
      value: provider.id.toString(),
    })) ?? [];

    setClassicDropdownOptions(baseOptions);
    setMultiSelectOptions([
      { value: "", text: "All Providers" },
      ...baseOptions,
    ]);

    return locationName;
  },[]);

  const handleMultiSelectProvider = ( selections: MultiSelectDropdownOptions[] ) => {

    /*
      Build out the grouping for the scheduler by provider
    */
    const multiSelect = selections.filter((selction) => selction.value !== ""); //Filter out select all users since the value is just a placeholder
    const multiSelectFormat = multiSelect.map((selction) => ({
      providerId: parseInt(selction.value),
      providerName: selction.text,
    }));

    /*
      Extract the provider ids from the multiSelect array and join them into a string to pass to the API
    */
    const providerIdArr = multiSelect.map((index) => index.value);
    const providerId = providerIdArr.join(",");

    setSelectedMultiProvider(selections);
    refreshCalendarEvents("getByLocationCodeProviderAsync", locationCode, providerId);
    setCalendarGrouping("provider", multiSelectFormat, "providerId", "providerName");

  };

  const handleClassicDropdownChange = (e: DropDownListChangeEvent) => {

    const {text: providerName, value: providerId}: { text: string; value: string } = e.value;

    //Return if selected provider is the default option
    if(providerId === "-1"){
      return;
    }

    setSelectedProvider(e.value.value)
    refreshCalendarEvents("getByLocationCodeProviderAsync", locationCode, providerId);
    setCalendarGrouping("provider", [], "providerId", "providerName", true);

  }
  const handleViewChange = (e: any) => {

    // Make sure to reset the selected provider dropdown if the user switches to week view and no providers are selected
    if( e.value === "week" && selectedMultiProvider.length === 0){
      setSelectedProvider("-1");
      setCalendarGrouping("provider", [], "providerId", "providerName", true);
      refreshCalendarEvents("getByLocationCodeProviderAsync", locationCode, "-1");
    }

    if( e.value === "week" && selectedMultiProvider.length > 0){
      /*
        If the user has selected all providers, we need to set the selected provider to the first one in the array
        and refresh the calendar events for that provider. To avoid selecting the all providers option,
        If the user has selected only one provider, we can just use that provider.
      */
      if(selectedMultiProvider[0].hasOwnProperty("items")){
        setSelectedProvider(selectedMultiProvider[1].value);
        refreshCalendarEvents("getByLocationCodeProviderAsync", locationCode, selectedMultiProvider[1].value);
      }else{
        setSelectedProvider(selectedMultiProvider[0].value);
        refreshCalendarEvents("getByLocationCodeProviderAsync", locationCode, selectedMultiProvider[0].value);
      }
      setCalendarGrouping("provider", [], "providerId", "providerName", true);
    }

    if( e.value === "day" && selectedProvider !== ""){

      const  newSelect = classicDropdownOptions.find((option) => option.value === selectedProvider);
      const exist = selectedMultiProvider.some((option) => option.value === newSelect?.value);

      let updatedMultiSelect;

      updatedMultiSelect = exist ? [...selectedMultiProvider] : [...selectedMultiProvider, newSelect];

      const multiSelect = updatedMultiSelect.filter((selction) => selction.value !== "");
      const multiSelectFormat = multiSelect.map((selction) => ({
        providerId: parseInt(selction.value),
        providerName: selction.text,
      }));

      const providerIdArr = multiSelect.map((index) => index.value);
      const providerId = providerIdArr.join(",");

      const setMulti = multiSelect.map((option) => ({
        text: option.text,
        value: option.value,
      }));

      // Check to see if the user has selected all providers, if so, we need to add the "All Providers" option to the beginning of the array
      if (setMulti.length === multiSelectOptions.length -1){
        setMulti.unshift({ text: "All Providers", value: "", items: setMulti } as MultiSelectDropdownOptions);
      }
      setSelectedMultiProvider(setMulti);
      refreshCalendarEvents("getByLocationCodeProviderAsync", locationCode, providerId);
      setCalendarGrouping("provider", multiSelectFormat, "providerId", "providerName");
    }

    setActiveView(e.value);
  }

  return(
    <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
    <div className="d-flex align-items-center flex-shrink-0">
      <div className="me-3">
        <h2>Calendar</h2>
        <p>{locationName}</p>
      </div>
      {
        activeView === "day" && (
          <div className="flex-grow-1">
            <MultiSelectProviderDropdown
              label=""
              name="providers"
              providers={multiSelectOptions}
              filterable={true}
              onChange={handleMultiSelectProvider}
              enableRef={true}
              value={selectedMultiProvider}
            />
          </div>)
      }
      {
        activeView === "week" && (
        <div className="flex-grow-1">
          <SearchableProviderDropdown
            defaultItem={true}
            name={"providers"}
            providers={classicDropdownOptions}
            value={selectedProvider}
            onChange = {handleClassicDropdownChange}
          />
        </div>
        )
      }
    </div>

    <div className="flex-grow-1" style={{ minHeight: 0 }}>
      <Scheduler
        data={mapResult}
        date={date}
        onDateChange={({ value }) => {
          handleInputChange(value, "dateSelection");
        }}
        onViewChange={handleViewChange}
        height="100%"
        group={mapResult.length > 0 ? group: null} //Reset the group if no data is available otherwise it will show empty time slots
        resources={resources}
        item={NameTitleTimeItem}
      >
        <DayView
          title="Day"
          numberOfDays={1}
          slotDuration={60}
          slotDivisions={4}
          startTime="07:00"
          endTime="19:00"
        />
        <WeekView
          title="Full Week"
          workWeekStart={Day.Sunday}
          workWeekEnd={Day.Saturday}
          item={NameTitleTimeItem}
        />
      </Scheduler>
    </div>
  </div>
  )
}


export default CalendarPage;
